import * as React from "react";
import { useRef, useState } from "react";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { vorgangService } from "../../../services/vorgangService";
import { DokumentAuswahlDialogV4 } from "./DokumentAuswahlDialogV4";
import { useWindowManager } from "../../windowManager/WindowManagerContext";
import { openComposeWindow } from "../../mail/ComposeMailWindow";
import { buildMailSubjectPrefill } from "../../mail/mailSubjectSchema";
import { useTelephony } from "../../../context/TelephonyContext";
import { KontaktAktionen } from "../detail/KontaktAktionen";
import { AddMailFromMailboxDialog } from "../detail/AddMailFromMailboxDialog";
import { fehlschlaegeText, uploadMehrereDokumente } from "../detail/mehrfachUpload";
import { LinkSelectionDialog } from "../detail/LinkRow";
import type { CascadeResult, LinkSlots, RisikoRef, VertragRef } from "../detail/linkSlots";
import { risikenGewaehlt, vertraegeGewaehlt } from "../v3/bezugAuswahl";
import { RdPopover } from "../../redesign/table/RdPopover";
import { IcoAnruf, IcoDokument, IcoKalender, IcoMail, IcoPlus, IcoPosteingang, IcoUpload } from "../v3/vg3Icons";
import { NeuesOfficeDokumentDialog } from "../../neuAnlage/NeuesOfficeDokumentDialog";
import {
    editorOeffnen,
    editorTabVerwerfen,
    editorTabVorbereiten,
    useOfficeVerfuegbar,
} from "../../neuAnlage/officeDokument";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { hinweisen } from "../../redesign/bestaetigen";

/**
 * Die Aktionen eines Arbeitsstrangs als LISTE, nicht als fertige Leiste.
 *
 * Sie haben seit dem 13.09.2026 zwei Wirte: die Fußleiste (`AktionsLeisteV4`,
 * waagerecht) und das `⊕` in der Betreffzeile (`PlusMenueV4`, senkrecht). Der
 * Grund für den zweiten Weg steht im Transkript vom 13.09.2026 — bei einem
 * langen Vorgang liegt der Fuß zu weit unten: „damit ich es nur schnell
 * arbeiten kann, falls so ein Ding lang ist“. Der Fuß-Anlegekasten BLEIBT, das
 * `⊕` ist der zweite Weg, nicht der Ersatz.
 *
 * SEIT T-302 (PO 29.10.2026: „Das soll den + Button im Vorgang ersetzen")
 * steht im Vorgangskopf und auf den To-do-Karten statt des `⊕` das A des
 * Austauschs (`AustauschKnopfV4`). Die Fußleiste ist damit dort wieder der
 * eine Weg; das `⊕` trägt nur noch die Vorgangs-Sonderkarte
 * (`VorgangAlsTodoKarteV4`), in der es keinen Austausch gibt.
 *
 * Seit dem Archiv am Seitenende (11.09.2026, `ArchivAbschnitt`) sind es vier
 * Einträge: „Alle Mails“ und „Alle Dokumente“ standen hier bis dahin als
 * Nachschlage-Knöpfe — das Archiv trägt dieselbe Liste vollständiger und an
 * einer Stelle statt zweier.
 *
 * Zwei Listen mit denselben Knöpfen wären nach dem ersten neuen Knopf
 * auseinandergelaufen — deshalb liegt hier die Quelle und dort nur noch die
 * Darstellung. Was Zustand braucht (Mail-Picker, Upload-Fortschritt), gehört
 * zum Hook und wird vom Wirt als `elemente` gerendert — AUSSERHALB eines
 * Popovers, sonst verschwände der Dialog mit ihm.
 */
export type AktionsKnopf = {
    key: string;
    label: string;
    icon: React.ReactNode;
    title?: string;
    /**
     * Verlangt Bearbeitungsrecht. Seit das Nachschlagen ins Archiv gewandert
     * ist, trifft das auf JEDEN Eintrag zu — das Merkmal bleibt trotzdem
     * stehen: das `⊕` zeigt genau die Einträge, die etwas HINZUFÜGEN, und der
     * Rückfallweg (die Nachschlage-Knöpfe kehren zurück und springen ins
     * Archiv) käme sonst ohne Unterscheidung zurück.
     */
    nurMitRecht: boolean;
    disabled?: boolean;
    /**
     * Zahl hinter der Beschriftung. Derzeit setzt sie kein Eintrag — sie stand
     * an den Nachschlage-Knöpfen („Alle Mails 12“) und bleibt für deren
     * möglichen Rückfallweg stehen.
     */
    anzahl?: number;
    onClick?: () => void;
    /**
     * Einträge, die ihren Knopf selbst mitbringen: der Anruf ist kein reiner
     * Klick, sondern lädt den Kontakt nach und lässt zwischen Zentrale und
     * Ansprechpartnern wählen (`KontaktAktionen`). Der Wirt reicht nur seine
     * Knopfklasse hinein.
     */
    eigenerKnopf?: (o: { className: string }) => React.ReactNode;
};

export type AktionsKnoepfeOptionen = {
    vorgangId: number;
    todoId?: number;
    contactId?: number | null;
    contactName?: string | null;
    /** Bausteine des Betreff-Schemas ausgehender Mails. */
    vorgangNbr?: string | null;
    vertragNr?: string | null;
    risikoName?: string | null;
    /** Bereits verknüpfte messageIds — der Picker graut sie aus. */
    zugewieseneMessageIds: Set<string>;
    /**
     * Dokumente, die im Strom schon liegen — Heimat wie Gast. Die Auswahl
     * graut sie aus („hängt hier schon"), statt sie erneut anzubieten.
     */
    vorhandeneDokumentIds?: ReadonlySet<number>;
    onGeaendert: () => void | Promise<void>;
};

/**
 * Was das Plus auf dem Handy auslösen kann (Plan „Vorgangsdetail mobil",
 * PR 3) — dieselben Wege wie die Knöpfe, nur mit dem Ziel ZUR LAUFZEIT.
 * `datei` öffnet direkt den Dateidialog des Geräts (dort bieten iOS und
 * Android selbst Kamera, Fotos und Dateien an).
 */
export type AktionsSchluessel = "mail-neu" | "mail-zuordnen" | "datei" | "dokument-neu" | "office-neu" | "termin-neu";

/** Ziel eines Laufs: `todoId` gesetzt = dieses To-do, sonst der Vorgang. */
export type AktionsZiel = { todoId?: number | null };

export type AktionsErgebnis = {
    /** Hochgeladen bzw. zugeordnet — für den Hinweis „… in ‚X‘". */
    art: "dokumente" | "mail";
    anzahl: number;
    ziel: AktionsZiel;
};

export function useAktionsKnoepfe({
    vorgangId,
    todoId: gebundenesTodoId,
    contactId,
    contactName,
    vorgangNbr,
    vertragNr,
    risikoName,
    zugewieseneMessageIds,
    vorhandeneDokumentIds,
    onGeaendert,
    onErgebnis,
}: AktionsKnoepfeOptionen & {
    /** Meldet einen abgeschlossenen Lauf (Plus auf dem Handy: Hinweis). */
    onErgebnis?: (e: AktionsErgebnis) => void;
}): {
    knoepfe: AktionsKnopf[];
    elemente: React.ReactNode;
    /** Eine Aktion mit Ziel zur Laufzeit (Plus auf dem Handy). */
    fuehreAus: (key: AktionsSchluessel, ziel: AktionsZiel) => void;
    officeVerfuegbar: boolean;
} {
    /*
     * DAS ZIEL ZUR LAUFZEIT (Plan „Vorgangsdetail mobil", PR 3). Die Knöpfe
     * sind an EIN Ziel gebunden (`todoId` der Optionen). Das Plus wählt das
     * Ziel erst beim Tippen — `fuehreAus` merkt es hier, und die Dialoge
     * unten lesen es. Ein gebundener Knopf setzt es zurück.
     */
    const [laufZiel, setLaufZiel] = useState<AktionsZiel | null>(null);
    const laufZielRef = useRef<AktionsZiel | null>(null);
    const todoId: number | undefined =
        laufZiel != null ? (laufZiel.todoId ?? undefined) : gebundenesTodoId;
    const setZiel = (z: AktionsZiel | null) => {
        laufZielRef.current = z;
        setLaufZiel(z);
    };
    const { openWindow } = useWindowManager();
    const { callNumber } = useTelephony();
    const [mailPicker, setMailPicker] = useState(false);
    const [dokumentAuswahl, setDokumentAuswahl] = useState(false);
    const [officeNeu, setOfficeNeu] = useState(false);
    const officeVerfuegbar = useOfficeVerfuegbar();
    const [upload, setUpload] = useState<{ fertig: number; gesamt: number } | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    const mailSchreiben = (zielTodo: number | undefined = gebundenesTodoId) => {
        const todoId = zielTodo;
        const { subject } = buildMailSubjectPrefill({
            vorgangNbr,
            kunde: contactName,
            vsnr: vertragNr,
            risikoobjekt: risikoName,
        });
        openComposeWindow(openWindow, {
            vorgangId,
            // T-259: an einer To-do-Karte gehört die Mail diesem To-do.
            ...(todoId != null ? { todoId } : {}),
            subject,
            contactId,
            // T-22: „An"/„Cc" schlagen beim Klick die Adressen des Kunden vor.
            empfaengerKontextId: contactId,
            onSent: () => void onGeaendert(),
        });
    };

    const dokumenteHochladen = async (files: File[]) => {
        if (files.length === 0 || upload) return;
        // Der Dateidialog kommt erst nach dem Tippen zurück — das Ziel steht
        // im Ref, nicht im (evtl. noch alten) Render.
        const lauf = laufZielRef.current;
        const todoId = lauf != null ? (lauf.todoId ?? undefined) : gebundenesTodoId;
        setUpload({ fertig: 0, gesamt: files.length });
        try {
            const ergebnis = await uploadMehrereDokumente(
                files,
                async (file) => {
                    await documentService.upload({
                        file,
                        ...(todoId != null ? { todoId } : { vorgangId }),
                    });
                },
                (fertig, gesamt) => setUpload({ fertig, gesamt }),
            );
            await onGeaendert();
            if (ergebnis.erfolgreich > 0) {
                onErgebnis?.({ art: "dokumente", anzahl: ergebnis.erfolgreich, ziel: { todoId: todoId ?? null } });
            }
            if (ergebnis.fehlgeschlagen.length > 0) {
                console.error("[aktionsKnoepfe] Uploads fehlgeschlagen", {
                    vorgangId,
                    todoId: todoId ?? null,
                    anzahl: ergebnis.fehlgeschlagen.length,
                });
                void hinweisen({
                    titel: "Nicht alle Dokumente hochgeladen",
                    text:
                        `${ergebnis.erfolgreich} von ${files.length} Dokumenten hochgeladen. Fehlgeschlagen:\n` +
                        fehlschlaegeText(ergebnis.fehlgeschlagen),
                    ton: "danger",
                });
            }
        } finally {
            setUpload(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    /**
     * Das Ergebnis der Auswahl: zwei Quellen, zwei Wege.
     *
     * Dateien vom Rechner werden hochgeladen wie bisher. Dokumente, die schon
     * in KMVS liegen, werden VERKNÜPFT und nicht umgehängt — ein umgehängtes
     * Kundendokument verschwände aus der Akte, sobald dieser Vorgang in den
     * Papierkorb wandert.
     */
    const dokumenteUebernehmen = async (dokumente: DocumentDto[], dateien: File[]) => {
        if (dokumente.length > 0) {
            try {
                await vorgangService.verknuepfeDokumente(
                    vorgangId,
                    dokumente.map((d) => d.id),
                    todoId ?? null,
                );
            } catch (err: any) {
                console.error("[aktionsKnoepfe] Verknüpfen fehlgeschlagen", { vorgangId, err });
                // Es wurde nichts geschrieben: der Server bricht die ganze
                // Anfrage ab, sobald ein Dokument nicht erlaubt ist.
                void hinweisen({
                    titel: "Dokumente nicht hinzugefügt",
                    text: err?.message ?? "Die Dokumente konnten nicht hinzugefügt werden.",
                    ton: "danger",
                });
            }
        }
        // Der Upload zieht `onGeaendert` selbst nach; ohne Dateien holen wir
        // den Strom hier, damit die Verknüpfungen sofort sichtbar sind.
        if (dateien.length > 0) await dokumenteHochladen(dateien);
        else if (dokumente.length > 0) await onGeaendert();
    };

    /**
     * Die Reihenfolge ist die der Fußleiste, um die beiden ins Archiv
     * gewanderten Nachschlage-Einträge gekürzt:
     * Neue Mail · Mail hinzufügen · Anruf · Dokument. Das `⊕` zeigt dieselbe
     * Reihe in derselben Reihenfolge — so steht dieselbe Sache an beiden
     * Orten gleich.
     */
    const knoepfe: AktionsKnopf[] = [];

    const terminAnlegen = (zielTodo: number | undefined) => {
        void (async () => {
            const { openTerminFormWindow } = await import(
                "../../windowManager/openTerminFormWindow"
            );
            openTerminFormWindow(openWindow, {
                linkVorgangId: vorgangId,
                ...(zielTodo != null ? { linkTodoId: zielTodo } : {}),
                contactId: contactId ?? null,
                contactName: contactName ?? null,
            });
        })();
    };

    knoepfe.push({
        key: "mail-neu",
        label: "Neue Mail",
        icon: <IcoMail />,
        nurMitRecht: true,
        // Der Mail-VERSAND kennt nur `vorgangId` (`POST /mail/send`) — eine
        // hier verfasste Mail hängt also immer am Vorgang, auch wenn sie am
        // To-do ausgelöst wurde. Der Tooltip sagt das.
        title:
            todoId != null
                ? "Neue Mail schreiben — sie wird dem Vorgang zugeordnet, nicht dem To-do"
                : "Neue Mail schreiben",
        onClick: () => {
            setZiel(null);
            mailSchreiben();
        },
    });

    knoepfe.push({
        key: "mail-zuordnen",
        label: "Mail hinzufügen",
        icon: <IcoPosteingang />,
        nurMitRecht: true,
        title: "Eine vorhandene Mail aus einem Postfach zuordnen",
        onClick: () => {
            setZiel(null);
            setMailPicker(true);
        },
    });

    knoepfe.push({
        key: "anruf",
        label: "Anruf",
        icon: <IcoAnruf />,
        nurMitRecht: true,
        ...(contactId != null
            ? {
                  eigenerKnopf: ({ className }) => (
                      <KontaktAktionen
                          kontaktId={contactId}
                          kontaktName={contactName ?? null}
                          nur="phone"
                          label="Anruf"
                          variant="btn"
                          // `.vgd-btn` liest `--vgd-*`, die es nur unter `.vgd`
                          // gibt — hier stünde der Knopf sonst ohne Rand neben
                          // den anderen.
                          className={className}
                          icon={<IcoAnruf />}
                          onCompose={() => undefined}
                          // Aus dem Vorgang gewählt → die Nach-Anruf-Karte
                          // schlägt genau diesen Vorgang/dieses To-do vor.
                          onCall={(nummer, name) =>
                              callNumber(nummer, name, { vorgangId, todoId: todoId ?? null })
                          }
                      />
                  ),
              }
            : { disabled: true, title: "Ohne Kunden gibt es keine Nummer" }),
    });

    /*
     * EIN KNOPF FÜR DREI QUELLEN (PO 22.09.2026).
     *
     * Bis hierher führte „Dokument" direkt in den Dateidialog des Browsers —
     * ein Dokument, das schon in KMVS lag, musste erst heruntergeladen und
     * wieder hochgeladen werden. Jetzt öffnet er die Auswahl, in der die
     * Kundenakte, die anderen Vorgänge und der Rechner nebeneinander stehen.
     * Drag & Drop auf den Strang (`DateiDropZoneV4`) bleibt der schnelle Weg
     * für eine Datei, die ohnehin schon auf dem Schreibtisch liegt.
     */
    knoepfe.push({
        key: "dokument-neu",
        label: upload ? `Lädt… ${upload.fertig}/${upload.gesamt}` : "Dokument",
        icon: <IcoUpload />,
        nurMitRecht: true,
        title: "Dokument hinzufügen — aus KMVS oder vom Rechner",
        disabled: !!upload,
        onClick: () => {
            setZiel(null);
            setDokumentAuswahl(true);
        },
    });

    /*
     * NEUES DOKUMENT (User 28.09.2026: „bei Vorgängen neben Dokument hochladen
     * auch neues Dokument und dann fragen Excel oder Word und Namen eingeben").
     *
     * Legt ein leeres Word/Excel an diesem Strang an — am To-do, wenn der
     * Strang eines ist, sonst am Vorgang; genau wie der Upload daneben. Ein
     * Kontakt wird bewusst NICHT mitgeschickt: Vorgangsdokumente erscheinen
     * über den Vorgang ohnehin in der Kundenakte, und der Upload tut es auch
     * nicht. Ohne Office-Editor auf dem Server fehlt der Knopf ganz — eine
     * Datei, die sich nicht öffnen lässt, ist kein Angebot.
     */
    if (officeVerfuegbar) {
        knoepfe.push({
            key: "office-neu",
            label: "Neues Dokument",
            icon: <IcoDokument />,
            nurMitRecht: true,
            title: "Leeres Word- oder Excel-Dokument anlegen und im Editor öffnen",
            onClick: () => {
                setZiel(null);
                setOfficeNeu(true);
            },
        });
    }

    /*
     * NEUER TERMIN (PO 18.09.2026, Zettel: „Neuer Termin in Aktionen").
     *
     * Bis dahin ließ sich aus V4 kein Termin anlegen — nur zuweisen, öffnen
     * und entfernen; angelegt wurde im Kalender oder im alten Reiter. Der
     * Termin entsteht im schlanken Create-Dialog und hängt danach an diesem
     * Strang: am Vorgang, und mit `todoId` zusätzlich an diesem To-do
     * (`linkTodoId`). Anders als beim Mail-VERSAND, der nur `vorgangId` kennt,
     * landet er also wirklich dort, wo er ausgelöst wurde.
     *
     * Die Karte erscheint danach im Verlauf, auch ohne Notiz
     * (`terminImVorgang`, seit 27.10.2026).
     */
    knoepfe.push({
        key: "termin-neu",
        label: "Neuer Termin",
        icon: <IcoKalender />,
        nurMitRecht: true,
        title:
            todoId != null
                ? "Termin anlegen — er wird diesem To-do zugeordnet"
                : "Termin anlegen — er wird diesem Vorgang zugeordnet",
        /*
         * ERST BEIM KLICK GELADEN. Ein fester Import zöge das Termin-Fenster
         * samt Kalender und pdfjs in jeden Vorgang — die Aktionsleiste selbst
         * braucht davon nichts.
         */
        onClick: () => terminAnlegen(gebundenesTodoId),
    });

    const fuehreAus = (key: AktionsSchluessel, ziel: AktionsZiel) => {
        const t = ziel.todoId ?? undefined;
        switch (key) {
            case "mail-neu":
                mailSchreiben(t);
                return;
            case "termin-neu":
                terminAnlegen(t);
                return;
            case "datei":
                setZiel(ziel);
                // Synchron im Tipp — sonst blockt der Browser den Dateidialog.
                fileRef.current?.click();
                return;
            case "mail-zuordnen":
                setZiel(ziel);
                setMailPicker(true);
                return;
            case "dokument-neu":
                setZiel(ziel);
                setDokumentAuswahl(true);
                return;
            case "office-neu":
                setZiel(ziel);
                setOfficeNeu(true);
                return;
        }
    };

    const elemente = (
        <>
            <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => void dokumenteHochladen(Array.from(e.target.files ?? []))}
            />

            {dokumentAuswahl && (
                <DokumentAuswahlDialogV4
                    vorgangId={vorgangId}
                    contactId={contactId ?? null}
                    contactName={contactName ?? null}
                    todoId={todoId ?? null}
                    vorhandene={vorhandeneDokumentIds ?? new Set<number>()}
                    onClose={() => setDokumentAuswahl(false)}
                    onUebernehmen={({ dokumente, dateien }) =>
                        dokumenteUebernehmen(dokumente, dateien)
                    }
                />
            )}

            {officeNeu && (
                <NeuesOfficeDokumentDialog
                    zielName={
                        todoId != null
                            ? "Am To-do"
                            : vorgangNbr
                              ? `Am Vorgang ${vorgangNbr}`
                              : "Am Vorgang"
                    }
                    anlegen={async ({ art, name }) => {
                        const dok = await documentService.createOffice({
                            art,
                            name,
                            ...(todoId != null ? { todoId } : { vorgangId }),
                        });
                        return dok.id;
                    }}
                    tabVorbereiten={editorTabVorbereiten}
                    oeffnen={editorOeffnen}
                    tabVerwerfen={editorTabVerwerfen}
                    onAngelegt={() => onGeaendert()}
                    onClose={() => setOfficeNeu(false)}
                />
            )}

            {mailPicker && (
                <AddMailFromMailboxDialog
                    {...(todoId != null ? { todoId } : { vorgangId })}
                    contactId={contactId}
                    assignedMessageIds={zugewieseneMessageIds}
                    onClose={() => setMailPicker(false)}
                    onLinksChanged={async () => {
                        await onGeaendert();
                        onErgebnis?.({ art: "mail", anzahl: 1, ziel: { todoId: todoId ?? null } });
                    }}
                />
            )}
        </>
    );

    return { knoepfe, elemente, fuehreAus, officeVerfuegbar: officeVerfuegbar === true };
}

/**
 * Ein Eintrag als Knopf — dieselbe Definition, die Klasse des Wirts.
 *
 * `vorKlick` schließt beim `⊕` das Popover, BEVOR die Aktion läuft; in der
 * Fußleiste bleibt es leer.
 */
export function aktionsKnopfNode(
    k: AktionsKnopf,
    o: { className: string; mitAnzahl?: boolean; vorKlick?: () => void },
): React.ReactNode {
    if (k.eigenerKnopf) {
        return <React.Fragment key={k.key}>{k.eigenerKnopf({ className: o.className })}</React.Fragment>;
    }
    return (
        <button
            key={k.key}
            type="button"
            className={o.className}
            disabled={k.disabled}
            title={k.title}
            onClick={() => {
                o.vorKlick?.();
                k.onClick?.();
            }}
        >
            {k.icon} {k.label}
            {o.mitAnzahl && k.anzahl ? <span className="vg3-badge">{k.anzahl}</span> : null}
        </button>
    );
}

type PlusProps = AktionsKnoepfeOptionen & {
    canEdit: boolean;
    /** Bezüge des Strangs — für „Risiko zuweisen“ / „Vertrag zuweisen“. */
    slots: LinkSlots;
    onAnwendenLinks: (result: CascadeResult) => void | Promise<void>;
    /**
     * Persönlicher Vorgang: dann entfallen „Risiko zuweisen“ und „Vertrag
     * zuweisen“. Er trägt KEINE Bezüge — das Backend lehnt jeden Kunden-,
     * Risiko- oder Vertragsbezug mit 400 ab („Ein persönlicher Vorgang trägt
     * keine Bezüge.“, `VorgangService.update`). Ein Eintrag, der im Fehler
     * endet, ist kein Angebot. Die übrigen Aktionen (Mail, Anruf, Dokument)
     * bleiben — die funktionieren auch ohne Kunden.
     */
    persoenlich?: boolean;
};

/**
 * Das `⊕` in der Betreffzeile: alles Hinzufügen an EINER Stelle, oben, wo der
 * Betreff steht (Zeichnung 13.09.2026). Seit T-302 (29.10.2026) nur noch an
 * der Vorgangs-Sonderkarte — Kopf und To-do-Karten tragen dort das A.
 *
 * Neben den Aktionen des Strangs stehen darin „Risiko zuweisen“ und „Vertrag
 * zuweisen“ (Transkript 15:21: „Wie machen wir das denn, wenn ich das einem
 * Vertrag zuweisen will oder einem Risiko zuweisen will?“). Sie öffnen
 * denselben `LinkSelectionDialog` wie die Kreise daneben — der Weg über die
 * Kreise bleibt bestehen, das ist kein Ersatz.
 *
 * Ohne Bearbeitungsrecht gibt es nichts hinzuzufügen; dann erscheint das `⊕`
 * gar nicht erst.
 */
export const PlusMenueV4: React.FC<PlusProps> = ({
    canEdit,
    slots,
    onAnwendenLinks,
    persoenlich = false,
    ...optionen
}) => {
    const { knoepfe, elemente } = useAktionsKnoepfe(optionen);
    const [dialog, setDialog] = useState<"risiko" | "vertrag" | null>(null);

    if (!canEdit) return null;

    const anwenden = (result: CascadeResult) => {
        // Leerer Patch = unveränderte Auswahl → kein PUT.
        if (!Object.keys(result.patch).length) return;
        void onAnwendenLinks(result);
    };

    return (
        <>
            <RdPopover
                width={232}
                dialogLabel="Hinzufügen"
                align="end"
                trigger={({ toggle, ref }) => (
                    <button
                        ref={ref}
                        type="button"
                        className="vg3-ibtn vg4-plus"
                        title="Hinzufügen"
                        aria-label="Hinzufügen"
                        onClick={toggle}
                    >
                        <IcoPlus size={15} />
                    </button>
                )}
            >
                {(close) => (
                    // Portal an `body` — außerhalb des `.kvp-rd`-Wrappers der
                    // Seite; ohne eigene Hülle griffen weder Regeln noch Tokens.
                    <div className="kvp-rd vg3-menu vg4-plus-menu">
                        {knoepfe
                            .filter((k) => k.nurMitRecht)
                            .map((k) =>
                                aktionsKnopfNode(k, {
                                    className: "vg3-menu-item",
                                    // Der Anruf bringt seinen eigenen Knopf mit
                                    // und öffnet ggf. eine zweite Auswahl — er
                                    // schließt das Menü selbst, sobald gewählt
                                    // wurde. Alle anderen schließen sofort.
                                    ...(k.eigenerKnopf ? {} : { vorKlick: close }),
                                }),
                            )}

                        {/* Nur am Vorgang MIT Bezügen — siehe `persoenlich`. */}
                        {!persoenlich && (
                            <>
                                <button
                                    type="button"
                                    className="vg3-menu-item vg4-plus-trenner"
                                    onClick={() => {
                                        close();
                                        setDialog("risiko");
                                    }}
                                >
                                    Risiko zuweisen
                                </button>
                                <button
                                    type="button"
                                    className="vg3-menu-item"
                                    onClick={() => {
                                        close();
                                        setDialog("vertrag");
                                    }}
                                >
                                    Vertrag zuweisen
                                </button>
                            </>
                        )}
                    </div>
                )}
            </RdPopover>

            {dialog && (
                <LinkSelectionDialog
                    kind={dialog}
                    slots={slots}
                    onClose={() => setDialog(null)}
                    onKontakt={() => setDialog(null)}
                    onRisiken={(r: RisikoRef[]) => {
                        setDialog(null);
                        void risikenGewaehlt(slots, r).then(anwenden);
                    }}
                    onVertraege={(v: VertragRef[]) => {
                        setDialog(null);
                        void vertraegeGewaehlt(slots, v).then(anwenden);
                    }}
                />
            )}

            {elemente}
        </>
    );
};

